import api from './api';

// Helper function to parse model IDs
export function parseModelId(modelId: string): { provider: string, modelName: string } {
  if (!modelId) {
    return { provider: 'openrouter', modelName: '' };
  }
  
  // Check if it follows the provider://model_name format (for backward compatibility)
  const uriMatch = modelId.match(/^([a-zA-Z0-9_-]+):\/\/(.+)$/);
  if (uriMatch) {
    return {
      provider: uriMatch[1].toLowerCase(),
      modelName: uriMatch[2]
    };
  }
  
  // Check if it follows the provider/model_name format (standard format)
  const pathMatch = modelId.match(/^([a-zA-Z0-9_-]+)\/(.+)$/);
  if (pathMatch) {
    return {
      provider: pathMatch[1].toLowerCase(),
      modelName: pathMatch[2]
    };
  }
  
  // Default case for simple model names
  return { provider: 'openrouter', modelName: modelId };
}

// Helper function to create standardized model IDs
export function createModelId(provider: string, modelName: string): string {
  return `${provider}/${modelName}`;
}

export async function getModels(includeUnavailable = false, includeGreylisted = false) {
  const response = await api.get(`/models?include_unavailable=${includeUnavailable}&include_greylisted=${includeGreylisted}`);
  return response.data;
}

export async function getModelsByProvider(provider: string, includeUnavailable = false, includeGreylisted = false) {
  const response = await api.get(`/models?provider=${provider}&include_unavailable=${includeUnavailable}&include_greylisted=${includeGreylisted}`);
  return response.data;
}

export async function getCostThresholds() {
  const response = await api.get('/models/thresholds');
  return response.data;
}

export async function getModel(modelId: string) {
  const response = await api.get(`/models/${encodeURIComponent(modelId)}`);
  return response.data;
}

export async function syncModels() {
  const response = await api.get('/models/sync');
  return response.data;
}

export async function syncModelsNow(isAdmin: boolean = false) {
  console.log(`Calling syncModelsNow API (isAdmin: ${isAdmin})...`);
  try {
    const endpoint = isAdmin ? '/models/sync/now' : '/models/sync/ui';
    console.log(`Using endpoint: ${endpoint}`);
    const response = await api.get(endpoint);
    console.log("syncModelsNow API response:", response.data);
    return response.data;
  } catch (error) {
    console.error("syncModelsNow API error:", error);
    throw error;
  }
}

export async function getDefaultModel(entityType: string, entityId: string) {
  try {
    const response = await api.get(`/models/preferences/default?entity_type=${entityType}&entity_id=${entityId}`);
    
    // If the model ID is in the old format, convert it to the new format
    if (response.data && response.data.model) {
      const modelId = response.data.model.id;
      if (modelId.includes('://')) {
        // Parse and reformat to ensure consistency
        const { provider, modelName } = parseModelId(modelId);
        response.data.model.id = createModelId(provider, modelName);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("Error getting default model:", error);
    throw error;
  }
}

export async function setModelPreference(entityType: string, entityId: string, modelId: string, isDefault = true) {
  // Ensure model_id is in standardized format
  const { provider, modelName } = parseModelId(modelId);
  const standardizedModelId = createModelId(provider, modelName);
  
  const response = await api.post('/models/preferences', {
    entity_type: entityType,
    entity_id: entityId,
    model_id: standardizedModelId,
    is_default: isDefault
  });
  return response.data;
}

export async function deleteModelPreference(entityType: string, entityId: string, modelId: string) {
  // Ensure model_id is in standardized format
  const { provider, modelName } = parseModelId(modelId);
  const standardizedModelId = createModelId(provider, modelName);
  
  const response = await api.delete(
    `/models/preferences?entity_type=${entityType}&entity_id=${entityId}&model_id=${encodeURIComponent(standardizedModelId)}`
  );
  return response.data;
}

export async function estimateTokenUsage(
  modelId: string,
  inputText?: string,
  messages?: any[],
  estimatedOutputLength?: number
) {
  // Validate required parameters
  if (!modelId) {
    throw new Error("Model ID is required for token estimation");
  }
  
  if (!inputText && (!messages || messages.length === 0)) {
    throw new Error("Either input text or messages must be provided");
  }
  
  // Ensure model_id is in standardized format
  const { provider, modelName } = parseModelId(modelId);
  const standardizedModelId = createModelId(provider, modelName);
  
  // Build query parameters
  const queryParams = new URLSearchParams();
  queryParams.append('model_id', standardizedModelId);
  
  if (inputText) {
    queryParams.append('input_text', inputText);
  }
  
  if (estimatedOutputLength) {
    queryParams.append('estimated_output_length', estimatedOutputLength.toString());
  }
  
  // Only messages should be in the request body
  const body = messages && messages.length > 0 ? messages : null;
  
  console.log("Estimating token usage with params:", {
    queryParams: queryParams.toString(),
    body
  });
  
  try {
    const url = `/models/estimate-usage?${queryParams.toString()}`;
    const response = await api.post(url, body);
    return response.data;
  } catch (error: any) {
    // Extract the detailed error message if available
    const errorDetail = error.response?.data?.detail || error.message;
    console.error(`Token estimation error: ${errorDetail}`);
    throw new Error(errorDetail);
  }
}

export async function getSyncHistory() {
  console.log("Calling getSyncHistory API...");
  try {
    const response = await api.get('/models/sync/history');
    console.log("getSyncHistory API response:", response.data);
    return response.data;
  } catch (error) {
    console.error("getSyncHistory API error:", error);
    throw error;
  }
}

export async function getSyncHistoryFiles() {
  const response = await api.get('/models/sync/history/files');
  return response.data;
}

export async function getSyncHistoryFileContent(filename: string) {
  const response = await api.get(`/models/sync/history/files/${filename}`);
  return response.data;
}

export async function getModelPriceTrends(provider?: string | null, days: number = 30) {
  let url = `/models/sync/history/price-trends?days=${days}`;
  if (provider) url += `&provider=${provider}`;
  
  const response = await api.get(url);
  return response.data;
}

// Ollama-specific methods
export async function getOllamaModels(includeUnavailable = false) {
  const response = await api.get(`/models/ollama?include_unavailable=${includeUnavailable}`);
  
  // Convert to standardized format
  const models = response.data.map((model: any) => ({
    ...model,
    meta_data: {
      ...(model.meta_data || {}),
      standard_id: createModelId('ollama', model.name)
    }
  }));
  
  return models;
}

export async function syncOllamaModels() {
  const response = await api.get('/models/ollama/sync');
  return response.data;
}

export async function syncOllamaModelsNow() {
  try {
    const response = await api.get('/models/ollama/sync/now');
    return response.data;
  } catch (error) {
    console.error("syncOllamaModelsNow API error:", error);
    throw error;
  }
}

export async function getOllamaStats() {
  const response = await api.get('/models/ollama/stats');
  return response.data;
}

export async function pullOllamaModel(modelName: string) {
  const response = await api.post(`/models/ollama/pull/${modelName}`);
  return response.data;
}

export async function deleteOllamaModel(modelName: string) {
  const response = await api.delete(`/models/ollama/${modelName}`);
  return response.data;
}
